import { useEffect, useState } from "react";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { CgDetailsMore } from "react-icons/cg";
import { HiDocumentReport } from "react-icons/hi";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import documentApi from "../../../api/documentapi";
import Pagination from "../../../components/Pagination";
import ReminderModal from "../ReminderModal";
import SearchBar from "../SearchBar";

const MyConsents = () => {
  const navigate = useNavigate();
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // track the last submitted search
  const user = JSON.parse(localStorage.getItem("user"));
  const senderEmail = user?.userEmail;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const fetchConsents = async () => {
      setLoading(true);
      try {
        let response;

        if (searchTerm.trim() !== "") {
          response = await documentApi.getSearchSentConsensts(
            senderEmail,
            searchTerm,
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          );
        } else {
          response = await documentApi.getMyConsents(
            senderEmail,
            page,
            pageSize,
            sortedColumn,
            sortOrder,
          );
        }

        const content = response?.data?.content;

        setConsents(Array.isArray(content) ? content : []);
        setTotalPages(response.data.totalPages || 0);
      } catch (error) {
        console.error("Failed to fetch consents:", error);
        setConsents([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };

    fetchConsents();
  }, [senderEmail, searchTerm, page, pageSize, sortedColumn, sortOrder]);

  const handleEmailClick = (doc) => {
    setSelectedDoc(doc);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDoc(null);
  };

  const handleSendReminder = (docId) => {
    console.log("Sending email reminder for document:", docId);
    setShowModal(false);
  };
  const handleSearch = (value) => {
    setPage(0); // reset to first page on new search
    setSearchTerm((value ?? searchQuery).trim()); // set searchTerm to trigger search
  };

  const handleSendToSigners = async (documentId) => {
    try {
      const response = await documentApi.sendToSigners(documentId);
      Swal.fire("Success", response.data, "success");

      // Update the document status in local state without refetching all
      setConsents((prev) =>
        prev.map((doc) =>
          doc.documentId === documentId
            ? { ...doc, documentStatus: "SENT_TO_SIGNERS" }
            : doc,
        ),
      );
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data || "Failed to send document to signers.",
        "error",
      );
    }
  };

  const handleTask = (documentId, documentName) => {
    navigate(
      `/dashboard/my-consents/consent-object?documentId=${documentId}&documentName=${documentName}&tab=brief`,
    );
  };

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0);
  };

  const handlePageClick = (data) => {
    const selectedPage = Math.max(0, Math.min(data.selected, totalPages - 1)); // Ensure selectedPage is within range
    setPage(selectedPage);
    localStorage.setItem("all consents", selectedPage); // Store the page number in localStorage
  };

  const handleSort = (column) => {
    if (sortedColumn === column) {
      // Toggle sort order if the same column is clicked
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Sort by the new column (default to ascending)
      setSortedColumn(column);
      setSortOrder("asc");
    }
  };
  const handleDocumentClick = (consent) => {
    if (!consent.editedFile) {
      Swal.fire("Error", "Document file not available", "error");
      return;
    }

    try {
      const byteCharacters = atob(consent.editedFile);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      // PDF file
      const blob = new Blob([byteArray], {
        type: "application/pdf",
      });

      const fileURL = URL.createObjectURL(blob);

      window.open(fileURL, "_blank");
    } catch (error) {
      console.error("Failed to open document:", error);
      Swal.fire("Error", "Unable to open document", "error");
    }
  };
  return (
    <div
      style={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
        }}
      >
        <div style={{ width: "250px" }}>
          <SearchBar
            placeholder="Search consents..."
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch} // pass handler here
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <Table
          striped
          bordered
          hover
          style={{ minWidth: "1100px", whiteSpace: "nowrap" }}
        >
          <thead>
            <tr>
              <th>#id</th>
              <th
                onClick={() => handleSort("documentName")}
                style={{ cursor: "pointer" }}
              >
                Document Name
                <span>
                  <span
                    style={{
                      color:
                        sortedColumn === "documentName" && sortOrder === "asc"
                          ? "black"
                          : "gray",
                    }}
                  >
                    ↑
                  </span>{" "}
                  <span
                    style={{
                      color:
                        sortedColumn === "documentName" && sortOrder === "desc"
                          ? "black"
                          : "gray",
                    }}
                  >
                    ↓
                  </span>
                </span>
              </th>
              <th
                onClick={() => handleSort("companyName")}
                style={{ cursor: "pointer" }}
              >
                CompanyName
                <span>
                  <span
                    style={{
                      color:
                        sortedColumn === "companyName" && sortOrder === "asc"
                          ? "black"
                          : "gray",
                    }}
                  >
                    ↑
                  </span>{" "}
                  <span
                    style={{
                      color:
                        sortedColumn === "companyName" && sortOrder === "desc"
                          ? "black"
                          : "gray",
                    }}
                  >
                    ↓
                  </span>
                </span>
              </th>
              <th>Sent On</th>
              <th># of Reviewers count</th>
              <th># of signers count</th>
              {/* <th>Actions</th> */}
              {/* <th>Reviewer Status</th> */}
              <th>Document Status</th>
              {/* <th>Chat</th> */}
              <th>Actions</th>
              <th>Audit Trail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6">Loading consents...</td>
              </tr>
            ) : consents.length === 0 ? (
              <tr>
                <td colSpan="6">
                  {searchQuery && searchQuery.trim() !== ""
                    ? "No results found."
                    : "No consents found."}
                </td>
              </tr>
            ) : (
              consents.map((consent, index) => (
                <tr key={consent.documentId}>
                  {/* <td>{consent.documentId}</td> */}
                  <td>{page * pageSize + index + 1}</td>
                  <td>
                    <span
                      style={{
                        color: "blue",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => handleDocumentClick(consent)}
                    >
                      {consent.documentName}
                    </span>
                  </td>
                  <td>{consent.companyName}</td>
                  <td>{consent.sentOn}</td>
                  <td>
                    {consent.reviwercount} / {consent.totalReviwers}
                  </td>

                  <td>
                    {consent.signedCount} / {consent.totalSigners}
                  </td>
                  {/* <td>
                
                  <Button
                    variant="success"
                    size="sm"
                    className="me-2"
                    title="Download"
                  >
                    <AiOutlineDownload />
                  </Button>
                  <Button
                    variant="info"
                    size="sm"
                    title="Email"
                    onClick={() => handleEmailClick(consent)}
                  >
                    <MdEmail />
                  </Button>
                </td> */}
                  {/* <td>{consent.reviewerStatus}</td> */}

                  <td>
                    {/* UNDER REVIEW */}
                    {consent.documentStatus === "REVIEW" && (
                      <span
                        style={{
                          color: "orange",
                          fontWeight: "600",
                        }}
                      >
                        Under Review
                      </span>
                    )}

                    {/* PARTIALLY APPROVED */}
                    {consent.documentStatus === "PARTIALLY_APPROVED" && (
                      <span
                        style={{
                          color: "darkorange",
                          fontWeight: "600",
                        }}
                      >
                        Partially Approved
                      </span>
                    )}

                    {/* WAITING FOR SIGNERS */}
                    {consent.documentStatus === "AWAITING_SIGNING" && (
                      <span
                        style={{
                          color: "blue",
                          fontWeight: "600",
                        }}
                      >
                        Awaiting Signing
                      </span>
                    )}

                    {/* PARTIALLY SIGNED */}
                    {consent.documentStatus === "PARTIALLY_SIGNED" && (
                      <span
                        style={{
                          color: "purple",
                          fontWeight: "600",
                        }}
                      >
                        Partially Signed
                      </span>
                    )}

                    {/* COMPLETED */}
                    {consent.documentStatus === "SIGNED" && (
                      <span
                        style={{
                          color: "green",
                          fontWeight: "600",
                        }}
                      >
                        Signed
                      </span>
                    )}
                  </td>
                  {/* <td>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      handleChat(consent.documentId, consent.documentName)
                    }
                    title="Chat"
                  >
                    <IoChatbubbles />
                  </Button>
                </td> */}

                  <td>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-task">My Consent</Tooltip>}
                    >
                      <Button
                        variant="info"
                        onClick={() =>
                          handleTask(consent.documentId, consent.documentName)
                        }
                      >
                        <CgDetailsMore />
                      </Button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-delete">Delete Consent</Tooltip>
                      }
                    >
                      <Button variant="danger" className="ms-2">
                        <MdDelete />
                      </Button>
                    </OverlayTrigger>
                  </td>
                  <td>
                    <Button
                      variant="dark"
                      onClick={() =>
                        navigate(
                          `/dashboard/my-consents/audit-trail?documentId=${consent.documentId}`,
                        )
                      }
                    >
                      <HiDocumentReport />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {consents.length > 0 && totalPages > 0 && (
        <div style={{ marginTop: "auto" }}>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            handlePageSizeChange={handlePageSizeChange}
            handlePageClick={handlePageClick}
          />
        </div>
      )}

      <ReminderModal
        show={showModal}
        onClose={handleCloseModal}
        documentId={selectedDoc?.documentId}
        documentName={selectedDoc?.documentName}
        onSend={handleSendReminder}
      />
    </div>
  );
};

export default MyConsents;
